import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { timezoneOptions, localTimezone } from '@/lib/timezones';
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  Plus, ChevronDown, Building2, MapPin, Layers, Square, Cctv, Boxes, Network as NetworkIcon,
  X, HardDrive, FolderTree, Tag, MoveRight, GripVertical, Trash2,
} from 'lucide-react';
import {
  locationsApi, sitesApi, placementApi, logicalGroupsApi, hierarchyApi,
  type TreeNode, type TreeNodeType, type Organization, type PlacementTarget,
  type LocationKind, type LocationImpact,
} from '@/lib/inventory-api';
import { ConfirmDialog } from '@/components/ui/dialogs';
import { PageHeader } from '@/components/ui/DataTable';
import { StatusPill, KpiStat, Skeleton, EmptyState } from '@/components/ui/primitives';
import { LocationPicker } from '@/components/LocationPicker';
import { GroupPicker } from '@/components/GroupPicker';
import { AddDeviceModal } from '@/components/AddDeviceModal';
import { PromptDialog, PickerDialog } from '@/components/ui/dialogs';

// Asset organization workspace. Three views (the user's terminology):
//   physical = the driver/hardware tree: Driver → NVR/DVR/VMS → channels
//   logical  = the sites hierarchy: Org → Site → Building → Floor → Zone → device
//   groups   = custom tag-groups a device can belong to many of
// Organize by DRAGGING a device from Physical/Unassigned onto a Logical node
// (place it) or a Group (tag it) — or via the per-node "⋯" menu (pickers).
type View = 'physical' | 'logical' | 'groups';

const NODE_ICON: Record<TreeNodeType, typeof Building2> = {
  organization: Boxes, site: MapPin, building: Building2, floor: Layers,
  zone: Square, group: Tag, device: Cctv, driver: HardDrive, unassigned: FolderTree,
};
const scoreColor = (s: number) => (s >= 90 ? 'text-ok' : s >= 70 ? 'text-warning' : 'text-critical');

const VIEW_META: Record<View, { label: string; icon: typeof Building2; hint: string }> = {
  physical: { label: 'Physical', icon: HardDrive, hint: 'Driver → NVR/DVR/VMS → channels (as discovered).' },
  logical: { label: 'Logical (Sites)', icon: FolderTree, hint: 'Drag a device onto a site/zone to place it.' },
  groups: { label: 'Groups', icon: Tag, hint: 'Drag a device onto a group to tag it.' },
};

function defaultExpanded(roots: TreeNode[]): Set<string> {
  const ids = new Set<string>();
  for (const r of roots) { ids.add(r.id); for (const c of r.children) ids.add(c.id); }
  return ids;
}

export function SitesPage() {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['locations-tree'], queryFn: locationsApi.tree, retry: false });
  const [view, setView] = useState<View>('logical');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<TreeNode | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  // Pending device for the menu-driven Place / Assign pickers.
  const [placeFor, setPlaceFor] = useState<TreeNode | null>(null);
  const [groupFor, setGroupFor] = useState<TreeNode | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Styled prompt/picker dialogs (replace native window.prompt). One at a time.
  const [prompt, setPrompt] = useState<{ title: string; label?: string; onSubmit: (v: string) => Promise<void> } | null>(null);
  const [picker, setPicker] = useState<{ title: string; options: Array<{ id: string; label: string }>; onPick: (id: string) => void } | null>(null);

  const logicalRoots = data?.logical ?? [];
  const physicalRoots = data?.physical ?? [];
  const groupRoots = data?.groups ?? [];

  useEffect(() => {
    if (logicalRoots.length || physicalRoots.length) {
      setExpanded((prev) => new Set([...prev, ...defaultExpanded(logicalRoots), ...defaultExpanded(physicalRoots)]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const toggle = (id: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isExpanded = (id: string) => expanded.has(id);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['locations-tree'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };
  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const place = async (deviceId: string, target: PlacementTarget) => {
    await placementApi.place(deviceId, target); refresh(); flash(t('sites.toast.devicePlaced'));
  };
  const unplace = async (deviceId: string) => {
    await placementApi.unplace(deviceId); refresh(); flash(t('sites.toast.removedFromSite'));
  };
  const assign = async (deviceId: string, groupId: string) => {
    await logicalGroupsApi.addMember(groupId, 'device', deviceId); refresh(); flash(t('sites.toast.assignedToGroup'));
  };

  // Deleting a location cascades to its children (DB-level ON DELETE CASCADE)
  // and unplaces any devices on it (SET NULL) — never deletes them. Fetch those
  // counts first so the confirmation states the real blast radius.
  const [deleteFor, setDeleteFor] = useState<TreeNode | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<LocationImpact | null>(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (!deleteFor) { setDeleteImpact(null); return; }
    let cancelled = false;
    void (async () => {
      try {
        const i = await hierarchyApi.locationImpact(deleteFor.type as LocationKind, deleteFor.id);
        if (!cancelled) setDeleteImpact(i);
      } catch { /* dialog falls back to generic copy */ }
    })();
    return () => { cancelled = true; };
  }, [deleteFor]);
  const doDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await hierarchyApi.deleteLocation(deleteFor.type as LocationKind, deleteFor.id);
      refresh(); flash(t('sites.toast.deleted', { name: deleteFor.name }));
      setDeleteFor(null);
    } catch {
      flash(t('sites.toast.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };
  // Create a child node in the LOGICAL tree under `parent` (or a top-level site
  // when parent is an org/none). The child type follows the hierarchy.
  const createChild = async (parent: TreeNode | null) => {
    const pType = parent?.type;
    const childType: TreeNodeType =
      !parent || pType === 'organization' ? 'site'
      : pType === 'site' ? 'building'
      : pType === 'building' ? 'floor'
      : pType === 'floor' ? 'zone'
      : 'zone';
    const refId = parent?.physicalRef?.id ?? parent?.id;
    const childTypeLabel = t(`sites.nodeType.${childType}`);
    if (childType === 'site') { setAddOpen(true); return; } // sites use the richer modal
    setPrompt({
      title: t('sites.prompt.newChildName', { type: childTypeLabel }),
      onSubmit: async (name) => {
        try {
          if (childType === 'building') await hierarchyApi.createBuilding(refId!, name);
          else if (childType === 'floor') await hierarchyApi.createFloor(refId!, name);
          else await hierarchyApi.createZone(refId!, name);
          refresh(); flash(t('sites.toast.childCreated', { type: childTypeLabel }));
        } catch { flash(t('sites.toast.childCreateFailed', { type: childTypeLabel })); }
      },
    });
  };
  const childLabel = (n: TreeNode | null): string => {
    const ct = !n || n.type === 'organization' ? 'site' : n.type === 'site' ? 'building' : n.type === 'building' ? 'floor' : n.type === 'floor' ? 'zone' : null;
    return ct ? t('sites.addChild', { type: t(`sites.nodeType.${ct}`) }) : '';
  };

  // ── Header "Add" menu: create a site / building / floor explicitly. ──
  // Building needs a parent site, floor needs a parent building — resolved from
  // the selected node when it matches, else the first/only candidate, else a
  // picker prompt.
  const allLogicalSites = (data?.logical ?? []).flatMap((o) => o.children.filter((c) => c.type === 'site'));
  const allBuildings = allLogicalSites.flatMap((s) => s.children.filter((c) => c.type === 'building'));
  const refOf = (n: TreeNode) => n.physicalRef?.id ?? n.id;
  // Resolve a parent from candidates: the selected node if it matches, the only
  // candidate, else open a styled picker. Invokes `then` with the chosen node.
  const pickParent = (candidates: TreeNode[], label: string, then: (parent: TreeNode) => void) => {
    if (candidates.length === 0) { flash(t('sites.prompt.createFirst', { type: label })); return; }
    if (selected && candidates.some((c) => c.id === selected.id)) { then(selected); return; }
    if (candidates.length === 1) { then(candidates[0]); return; }
    setPicker({
      title: t('sites.prompt.whichParent', { type: label }),
      options: candidates.map((c) => ({ id: c.id, label: c.name })),
      onPick: (id) => { const p = candidates.find((c) => c.id === id); if (p) then(p); },
    });
  };
  const promptBuildingName = (site: TreeNode) => setPrompt({
    title: t('sites.prompt.newBuildingName', { parent: site.name }),
    onSubmit: async (name) => {
      try { await hierarchyApi.createBuilding(refOf(site), name); refresh(); flash(t('sites.toast.buildingCreated')); }
      catch { flash(t('sites.toast.buildingCreateFailed')); }
    },
  });
  const promptFloorName = (building: TreeNode) => setPrompt({
    title: t('sites.prompt.newFloorName', { parent: building.name }),
    onSubmit: async (name) => {
      try { await hierarchyApi.createFloor(refOf(building), name); refresh(); flash(t('sites.toast.floorCreated')); }
      catch { flash(t('sites.toast.floorCreateFailed')); }
    },
  });
  const addBuilding = () => {
    if (selected?.type === 'site') promptBuildingName(selected);
    else pickParent(allLogicalSites, t('sites.nodeType.site'), promptBuildingName);
  };
  const addFloor = () => {
    if (selected?.type === 'building') promptFloorName(selected);
    else pickParent(allBuildings, t('sites.nodeType.building'), promptFloorName);
  };

  // KPI rollup from the logical (sites) tree.
  const log = data?.logical ?? [];
  const allSites = log.flatMap((o) => o.children.filter((c) => c.type === 'site'));
  const totalDevices = log.reduce((s, o) => s + o.deviceCount, 0);
  const unplaced = log.flatMap((o) => o.children.filter((c) => c.type === 'unassigned')).reduce((s, n) => s + n.deviceCount, 0);
  const globalScore = log.length
    ? Math.round(log.reduce((s, o) => s + o.score * (o.deviceCount || 1), 0) / log.reduce((s, o) => s + (o.deviceCount || 1), 0))
    : 0;

  // Drag a device node; drop on a logical node (place) or group (assign).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = (e: DragEndEvent) => {
    const deviceId = e.active.data.current?.deviceId as string | undefined;
    const drop = e.over?.data.current as { kind: 'place'; target: PlacementTarget } | { kind: 'assign'; groupId: string } | undefined;
    if (!deviceId || !drop) return;
    if (drop.kind === 'place') void place(deviceId, drop.target);
    else void assign(deviceId, drop.groupId);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="space-y-6">
        <PageHeader
          title={t('sites.title')}
          subtitle={t('sites.subtitle')}
          action={
            <span className="flex items-center gap-2">
              <button className={`tk-btn-ghost text-xs ${showGroups ? 'bg-white/10' : ''}`} onClick={() => setShowGroups((s) => !s)}><Tag size={14} /> {t('sites.actions.groups')}</button>
              {/* Add menu — site / building / floor, each with its icon. */}
              <div className="relative">
                <button className="tk-btn-ghost text-xs" onClick={() => setAddMenuOpen((o) => !o)}><Plus size={14} /> {t('sites.actions.add')} <ChevronDown size={12} /></button>
                {addMenuOpen && (
                  <div className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-white/10 bg-surface p-1 shadow-2xl">
                    <button className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-fg hover:bg-white/5" onClick={() => { setAddMenuOpen(false); setAddOpen(true); }}>
                      <MapPin size={14} className="text-primary" /> {t('sites.nodeType.site')}
                    </button>
                    <button className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-fg hover:bg-white/5" onClick={() => { setAddMenuOpen(false); void addBuilding(); }}>
                      <Building2 size={14} className="text-primary" /> {t('sites.nodeType.building')}
                    </button>
                    <button className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-fg hover:bg-white/5" onClick={() => { setAddMenuOpen(false); void addFloor(); }}>
                      <Layers size={14} className="text-primary" /> {t('sites.nodeType.floor')}
                    </button>
                  </div>
                )}
              </div>
              <button className="tk-btn-primary text-xs" onClick={() => setAddDeviceOpen(true)}><Plus size={14} /> {t('sites.actions.addDevice')}</button>
            </span>
          }
        />

        {addOpen && (
          <AddSiteModal onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); refresh(); queryClient.invalidateQueries({ queryKey: ['sites'] }); }} />
        )}
        {addDeviceOpen && (
          <AddDeviceModal onClose={() => setAddDeviceOpen(false)} onAdded={() => { setAddDeviceOpen(false); refresh(); }} />
        )}
        {placeFor && (
          <LocationPicker
            title={t('sites.placePickerTitle', { name: placeFor.name })}
            onClose={() => setPlaceFor(null)}
            onPlace={async (t) => { const d = placeFor; setPlaceFor(null); if (d) await place(d.physicalRef?.id ?? d.id, t); }}
          />
        )}
        {deleteFor && (
          <ConfirmDialog
            title={t('sites.deleteDialog.title', { name: deleteFor.name })}
            message={
              <span>
                {t('sites.deleteDialog.body')}
                {deleteImpact && (deleteImpact.buildings + deleteImpact.floors + deleteImpact.zones + deleteImpact.devices) > 0 && (
                  <span className="mt-2 block text-xs text-warning">
                    {[
                      deleteImpact.buildings && t('sites.deleteDialog.buildings', { count: deleteImpact.buildings }),
                      deleteImpact.floors && t('sites.deleteDialog.floors', { count: deleteImpact.floors }),
                      deleteImpact.zones && t('sites.deleteDialog.zones', { count: deleteImpact.zones }),
                    ].filter(Boolean).join(' · ')}
                    {deleteImpact.devices > 0 && (
                      <span className="mt-1 block text-muted">{t('sites.deleteDialog.devices', { count: deleteImpact.devices })}</span>
                    )}
                  </span>
                )}
              </span>
            }
            confirmLabel={t('sites.deleteDialog.confirm')}
            busy={deleting}
            onConfirm={() => { void doDelete(); }}
            onClose={() => setDeleteFor(null)}
          />
        )}
        {groupFor && (
          <GroupPicker
            onClose={() => setGroupFor(null)}
            onAssign={async (gid) => { const d = groupFor; setGroupFor(null); if (d) await assign(d.physicalRef?.id ?? d.id, gid); }}
          />
        )}
        {prompt && (
          <PromptDialog
            title={prompt.title} label={prompt.label}
            onSubmit={prompt.onSubmit} onClose={() => setPrompt(null)}
          />
        )}
        {picker && (
          <PickerDialog
            title={picker.title} options={picker.options}
            onPick={(o) => picker.onPick(o.id)} onClose={() => setPicker(null)}
          />
        )}
        {toast && <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-ok/90 px-4 py-2 text-sm text-white shadow-lg">{toast}</div>}

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiStat label={t('sites.kpi.sites')} value={allSites.length} tone="info" />
          <KpiStat label={t('sites.kpi.devices')} value={totalDevices} tone="info" />
          <KpiStat label={t('sites.kpi.unplaced')} value={unplaced} tone={unplaced ? 'warning' : 'ok'} />
          <KpiStat label={t('sites.kpi.globalHealth')} value={`${globalScore}%`} tone={globalScore >= 90 ? 'ok' : globalScore >= 70 ? 'warning' : 'critical'} />
        </div>

        {/* Side-by-side: LOGICAL (left, drop targets) + PHYSICAL (right, draggable devices) */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Logical (sites) tree */}
          <div className="tk-card">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium"><FolderTree size={15} className="text-primary" /> {t('sites.panel.logical')}</div>
              <button className="tk-btn-ghost px-2 py-1 text-xs" title={selected ? childLabel(selected) || t('sites.addChildHint') : t('sites.addSite')} onClick={() => createChild(selected)}>
                <Plus size={13} /> {selected ? (childLabel(selected) || t('sites.addSite')) : t('sites.addSite')}
              </button>
            </div>
            <p className="mb-2 text-xs text-muted">{t('sites.panel.logicalHint')}</p>
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
            ) : logicalRoots.length ? (
              <ul className="max-h-[60vh] overflow-y-auto text-sm">
                {logicalRoots.map((n) => (
                  <TreeRow key={n.id} node={n} depth={0} view="logical"
                    isExpanded={isExpanded} toggle={toggle}
                    selectedId={selected?.id ?? null} onSelect={setSelected}
                    onPlace={setPlaceFor} onAssign={setGroupFor} onUnplace={unplace} onDelete={setDeleteFor} />
                ))}
              </ul>
            ) : (
              <EmptyState icon={MapPin} title={t('sites.empty.sitesTitle')} hint={t('sites.empty.sitesHint')} />
            )}
          </div>

          {/* Physical (driver) tree */}
          <div className="tk-card">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium"><HardDrive size={15} className="text-primary" /> {t('sites.panel.physical')}</div>
              <button className="tk-btn-ghost px-2 py-1 text-xs" onClick={() => setAddDeviceOpen(true)}><Plus size={13} /> {t('sites.actions.addDevice')}</button>
            </div>
            <p className="mb-2 text-xs text-muted">{t('sites.panel.physicalHint')}</p>
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
            ) : physicalRoots.length ? (
              <ul className="max-h-[60vh] overflow-y-auto text-sm">
                {physicalRoots.map((n) => (
                  <TreeRow key={n.id} node={n} depth={0} view="physical"
                    isExpanded={isExpanded} toggle={toggle}
                    selectedId={selected?.id ?? null} onSelect={setSelected}
                    onPlace={setPlaceFor} onAssign={setGroupFor} />
                ))}
              </ul>
            ) : (
              <EmptyState icon={HardDrive} title={t('sites.empty.devicesTitle')} hint={t('sites.empty.devicesHint')} />
            )}
          </div>
        </div>

        {/* Groups (optional third panel) */}
        {showGroups && (
          <div className="tk-card">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium"><Tag size={15} className="text-primary" /> {t('sites.panel.groups')}</div>
            <p className="mb-2 text-xs text-muted">{t('sites.panel.groupsHint')}</p>
            {groupRoots.length ? (
              <ul className="max-h-[40vh] overflow-y-auto text-sm">
                {groupRoots.map((n) => (
                  <TreeRow key={n.id} node={n} depth={0} view="groups"
                    isExpanded={isExpanded} toggle={toggle}
                    selectedId={selected?.id ?? null} onSelect={setSelected}
                    onPlace={setPlaceFor} onAssign={setGroupFor} />
                ))}
              </ul>
            ) : <EmptyState icon={Tag} title={t('sites.empty.groupsTitle')} hint={t('sites.empty.groupsHint')} />}
          </div>
        )}
      </div>
    </DndContext>
  );
}

// A drop target wrapper for logical/group container nodes.
function DropTarget({
  id, data, children,
}: {
  id: string; data: { kind: 'place'; target: PlacementTarget } | { kind: 'assign'; groupId: string };
  children: (over: boolean) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id, data });
  return <div ref={setNodeRef}>{children(isOver)}</div>;
}

// One recursive tree row. Device rows are draggable; logical/group containers
// are drop targets.
function TreeRow({
  node, depth, view, isExpanded, toggle, selectedId, onSelect, onPlace, onAssign, onUnplace, onDelete, inheritedTarget,
}: {
  node: TreeNode; depth: number; view: View;
  isExpanded: (id: string) => boolean; toggle: (id: string) => void;
  selectedId: string | null; onSelect: (n: TreeNode) => void;
  onPlace: (n: TreeNode) => void; onAssign: (n: TreeNode) => void;
  onUnplace?: (deviceId: string) => void;
  onDelete?: (n: TreeNode) => void;
  inheritedTarget?: PlacementTarget; // the nearest enclosing location (so leaves are droppable too)
}) {
  const { t } = useTranslation('pages');
  const Icon = NODE_ICON[node.type];
  const hasChildren = node.children.length > 0;
  const open = isExpanded(node.id);
  const active = node.id === selectedId;
  const isDevice = node.type === 'device';

  // This node's own placement target (containers), used both as its drop data
  // and passed down so child rows (incl. camera leaves) drop to the same place.
  const ownTarget = view === 'logical' && node.physicalRef && node.physicalRef.type !== 'device'
    ? physicalRefToTarget(node.physicalRef) : undefined;
  const childTarget = ownTarget ?? inheritedTarget;

  // Drop target metadata: in the logical view EVERY row is a place-target —
  // containers resolve to their own location, leaves (cameras) to the enclosing
  // one — so you can drop a dragged camera anywhere in a site's region, not only
  // on the exact container text line. Groups → assign.
  const dropData =
    view === 'logical' && (ownTarget ?? inheritedTarget)
      ? { kind: 'place' as const, target: (ownTarget ?? inheritedTarget)! }
      : view === 'groups' && node.type === 'group'
        ? { kind: 'assign' as const, groupId: node.id }
        : null;
  // Only PHYSICAL-tree devices are draggable (drag from right → drop on left).
  const draggable = isDevice && view === 'physical';

  const row = (over: boolean) => (
    <div
      className={`group flex items-center gap-2 rounded-md py-1.5 pr-2 transition-colors ${active ? 'bg-white/10' : over ? 'bg-primary/20 ring-1 ring-primary/50' : 'hover:bg-white/5'}`}
      style={{ paddingLeft: depth * 18 + 4 }}
    >
      {hasChildren ? (
        <button onClick={() => toggle(node.id)} className="grid h-5 w-5 place-items-center rounded text-muted hover:text-fg" aria-label={open ? t('sites.aria.collapse') : t('sites.aria.expand')}>
          <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
        </button>
      ) : <span className="w-5" />}
      {draggable && <DeviceDragHandle deviceId={node.physicalRef?.id ?? node.id} />}
      <button onClick={() => onSelect(node)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <Icon size={15} className={isDevice ? scoreColor(node.score) : 'text-muted'} />
        <span className="truncate text-fg">{node.name}</span>
        {!isDevice && <span className="text-[11px] text-muted">({node.deviceCount})</span>}
        {/* PHYSICAL tree: show where a camera is already placed (or that it isn't). */}
        {isDevice && view === 'physical' && (
          node.placedAt
            ? <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary ring-1 ring-primary/20" title={t('sites.placedIn', { location: node.placedAt })}><MapPin size={10} /> {node.placedAt}</span>
            : <span className="shrink-0 rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-muted ring-1 ring-white/10">{t('sites.unplaced')}</span>
        )}
      </button>
      {isDevice && view === 'physical' && (
        <span className="hidden items-center gap-1 group-hover:flex">
          <button title={t('sites.actions.placeInHierarchy')} onClick={() => onPlace(node)} className="tk-btn-ghost px-1 py-0.5"><MoveRight size={13} /></button>
          <button title={t('sites.actions.assignToGroup')} onClick={() => onAssign(node)} className="tk-btn-ghost px-1 py-0.5"><Tag size={13} /></button>
        </span>
      )}
      {/* LOGICAL tree: remove a placed camera from its site (un-place). */}
      {isDevice && view === 'logical' && onUnplace && (
        <button title={t('sites.actions.removeFromSite')} onClick={() => onUnplace(node.physicalRef?.id ?? node.id)} className="hidden tk-btn-ghost px-1 py-0.5 text-critical group-hover:inline-flex"><X size={13} /></button>
      )}
      {/* Delete the location itself. Devices are never deleted — they are
          unplaced — but children DO cascade, so the dialog states the counts.
          Only the four levels that have a DELETE route: an organization has
          none, so offering it here would 404. */}
      {(['site', 'building', 'floor', 'zone'] as const).includes(node.type as 'site') && view === 'logical' && onDelete && (
        <button title={t('sites.actions.deleteNode')} onClick={() => onDelete(node)} className="hidden tk-btn-ghost px-1 py-0.5 text-critical group-hover:inline-flex"><Trash2 size={13} /></button>
      )}
      <span className={`tk-num text-xs font-medium ${scoreColor(node.score)}`}>{node.score}%</span>
      <StatusPill status={node.status} />
    </div>
  );

  return (
    <li>
      {dropData ? <DropTarget id={`drop-${node.id}`} data={dropData}>{row}</DropTarget> : row(false)}
      {hasChildren && open && (
        <ul>
          {node.children.map((c) => (
            <TreeRow
              key={c.id} node={c} depth={depth + 1} view={view}
              isExpanded={isExpanded} toggle={toggle} inheritedTarget={childTarget}
              selectedId={selectedId} onSelect={onSelect} onPlace={onPlace} onAssign={onAssign} onUnplace={onUnplace} onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function physicalRefToTarget(ref: { type: string; id: string }): PlacementTarget {
  switch (ref.type) {
    case 'site': return { siteId: ref.id };
    case 'building': return { buildingId: ref.id };
    case 'floor': return { floorId: ref.id };
    case 'zone': return { zoneId: ref.id };
    case 'organization': return { organizationId: ref.id };
    default: return {};
  }
}

// Draggable grip for a device row.
function DeviceDragHandle({ deviceId }: { deviceId: string }) {
  const { t } = useTranslation('pages');
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `dev-${deviceId}`, data: { deviceId } });
  return (
    <span
      ref={setNodeRef} {...attributes} {...listeners}
      className={`cursor-grab text-muted/60 hover:text-fg ${isDragging ? 'opacity-50' : ''}`}
      title={t('sites.dragHandleTitle')}
    >
      <GripVertical size={13} />
    </span>
  );
}

function NodeDetail({ node, onPlace, onAssign }: { node: TreeNode; onPlace: () => void; onAssign: () => void }) {
  const { t } = useTranslation('pages');
  const Icon = NODE_ICON[node.type];
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <span className={`grid h-10 w-10 place-items-center rounded-lg bg-white/5 ${scoreColor(node.score)}`}><Icon size={18} /></span>
        <div className="min-w-0">
          <div className="truncate font-medium">{node.name}</div>
          <div className="text-xs capitalize text-muted">{node.type}{node.coreType ? ` · ${node.coreType}` : ''}</div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted">{t('sites.detail.status')}</span>
        <StatusPill status={node.status} />
      </div>

      <div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">{t('sites.detail.healthRollup')}</span>
          <span className={`tk-num font-semibold ${scoreColor(node.score)}`}>{node.score}%</span>
        </div>
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.max(0, Math.min(100, node.score))}%`, background: node.score >= 90 ? '#22c55e' : node.score >= 70 ? '#eab308' : '#ef4444' }}
          />
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-y-2 text-xs">
        <dt className="text-muted">{t('sites.detail.type')}</dt>
        <dd className="text-right capitalize">{node.type}</dd>
        <dt className="text-muted">{t('sites.detail.devices')}</dt>
        <dd className="tk-num text-right">{node.deviceCount}</dd>
        {node.type !== 'device' && (
          <>
            <dt className="text-muted">{t('sites.detail.childNodes')}</dt>
            <dd className="tk-num text-right">{node.children.length}</dd>
          </>
        )}
      </dl>

      {node.type === 'device' && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <button className="tk-btn-ghost flex-1 justify-center" onClick={onPlace}><MoveRight size={14} /> {t('sites.detail.place')}</button>
            <button className="tk-btn-ghost flex-1 justify-center" onClick={onAssign}><Tag size={14} /> {t('sites.detail.tagGroup')}</button>
          </div>
          <Link to={`/devices/${node.physicalRef?.id ?? node.id}`} className="tk-btn-ghost w-full justify-center">{t('sites.detail.openDeviceDetail')}</Link>
        </div>
      )}
    </div>
  );
}

// Create-site modal — POSTs to the real, tenant-scoped /api/v1/sites (the site
// lands in the active customer's DB). Optionally targets a specific
// organization; otherwise the backend uses the customer's default org.
function AddSiteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation('pages');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  // Default to the operator's own zone — the common case for a first site.
  const [timezone, setTimezone] = useState(localTimezone);
  const [organizationId, setOrganizationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Org list for the optional target dropdown (real, tenant-scoped).
  const { data: orgs } = useQuery<Organization[]>({
    queryKey: ['organizations'],
    queryFn: sitesApi.organizations,
    retry: false,
  });

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError(t('sites.modal.nameRequired')); return; }
    setBusy(true);
    setError(null);
    try {
      await sitesApi.create({
        name: name.trim(),
        address: address.trim() || undefined,
        timezone: timezone.trim() || undefined,
        organizationId: organizationId || undefined,
      });
      onCreated();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ? t('sites.modal.createFailedWithReason', { reason: msg }) : t('sites.modal.createFailed'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('sites.modal.title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('sites.modal.close')}><X size={16} /></button>
        </div>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label htmlFor="site-name" className="mb-1 block text-sm text-muted">{t('sites.modal.nameLabel')}</label>
            <input
              id="site-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('sites.modal.namePlaceholder')}
              className="w-full rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50"
            />
          </div>
          <div>
            <label htmlFor="site-address" className="mb-1 block text-sm text-muted">{t('sites.modal.addressLabel')}</label>
            <input
              id="site-address" value={address} onChange={(e) => setAddress(e.target.value)}
              placeholder={t('sites.modal.addressPlaceholder')}
              className="w-full rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="site-tz" className="mb-1 block text-sm text-muted">{t('sites.modal.timezoneLabel')}</label>
              {/* Every IANA zone the browser knows (~418), west -> east. A free
                  text field here meant a typo'd zone silently produced wrong
                  local times on every schedule and report for that site. */}
              <select
                id="site-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}
                className="w-full rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50"
              >
                {timezoneOptions().map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="site-org" className="mb-1 block text-sm text-muted">{t('sites.modal.organizationLabel')}</label>
              <select
                id="site-org" value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}
                className="w-full rounded-md bg-white/5 px-3 py-2 text-sm ring-1 ring-white/10 focus:outline-none focus:ring-primary/50"
              >
                <option value="">{t('sites.modal.organizationDefault')}</option>
                {(orgs ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>
          {error && <p className="text-sm text-critical">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('sites.modal.cancel')}</button>
            <button type="submit" className="tk-btn-primary" disabled={busy}>
              {busy ? t('sites.modal.creating') : t('sites.modal.createSite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
